import "./ResourceManager.scss";
import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MotifDatePicker,
  MotifFormField,
  MotifTooltip,
} from "@ey-xd/motif-react";
import { Table } from "../../../components/CommonUIComponents/Table/Table";
import { SearchInput } from "../../../components/CommonUIComponents/SearchInput/SearchInput";
import { Select } from "../../../components/CommonUIComponents/Select/Select";
import { AssignEngagementManagerModal } from "../../../components/CommonUIComponents/AssignEngagementManagerModal/AssignEngagementManagerModal";
import { DownloadIcon, HelpIcon } from "../../../icons";
import { UserCircleIcon } from "../../../icons/userCircle";
import { useI18n } from "../../../hooks/useI18n";
import { handleFilterChange } from "../../../util/helpers";
import { toArrayOrNull, toDateString } from "../../../util/filterHelpers";
import { exportResourceManagerQueue } from "../../../store/services/resourceManager.service";
import { SEARCH_DEBOUNCE_MS, getColumnDefs } from "./ResourceManager.data";
import { routes } from "../../../routes/routes.constatns";
import {
  fetchQueue,
  fetchFilters,
  assignEM,
} from "../../../store/slices/resourceManagerSlice";
import { ToastMessage } from "../../../components/CommonUIComponents/ToastMessage/ToastMessage";
import { fetchCountriesThunk } from "../../../store/slices/userManagementSlice";

function ResourceManager() {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { domain } = useParams();

  const {
    rows,
    totalRecords,
    loading,
    filterOptions,
    assignLoading,
  } = useSelector((state) => state.resourceManager);
  const currentUser = useSelector((state) => state.user.user);
  const roleData = useSelector((state) => state.landing.roleData);

  const actingUserEmail = currentUser?.username || currentUser?.email || "";
  const assignedCountryIds = roleData?.processingTeamCountryIds || [];

  const userViews = roleData?.processingTeamType || [];

  const isGdsUser = userViews.includes("GDS");
  const isOnshoreUser = userViews.includes("Nearshore");


 const showProcessingViewDropdown = isGdsUser && isOnshoreUser;

  const [searchTerm, setSearchTerm] = useState("");
  const [superRegionFilter, setSuperRegionFilter] = useState([]);
  const [countryRegionFilter, setCountryRegionFilter] = useState([]);
  const [officeFilter, setOfficeFilter] = useState([]);
  const [sectorFilter, setSectorFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Unassigned");
  const [clientNameFilter, setClientNameFilter] = useState([]);

  const isAssigned = statusFilter === "Assigned";
  const [dateFilter, setDateFilter] = useState([null, null]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [exportLoading, setExportLoading] = useState(false);
  const [processingViewFilter, setProcessingViewFilter] = useState([]);


const { countries } = useSelector((state) => state.userManagement);

const assignedCountries = useMemo(() => {
  return countries.filter((country) =>
    assignedCountryIds.includes(country.countryId),
  );
}, [countries, assignedCountryIds]);

const processingViewId = useMemo(() => {
  if (isGdsUser && !isOnshoreUser) {
    return [0];
  }

  if (!isGdsUser && isOnshoreUser) {
    return assignedCountryIds;
  }

  if (isGdsUser && isOnshoreUser) {
    const selectedViews = Array.isArray(processingViewFilter)
      ? processingViewFilter
      : processingViewFilter
        ? [processingViewFilter]
        : [];

    if (selectedViews.length === 0) {
      return [0, ...assignedCountryIds];
    }

    return selectedViews
      .map((item) => {
        if (item === "GDS") {
          return 0;
        }

        return assignedCountries.find(
          (country) => country.countryName === item,
        )?.countryId;
      })
      .filter((id) => id !== undefined && id !== null);
  }

  return null;
}, [
  isGdsUser,
  isOnshoreUser,
  assignedCountryIds,
  processingViewFilter,
  assignedCountries,
]);

  useEffect(() => {
    if (location.state?.toast) {
      setToastMessage(location.state.toast);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.toast]);

  const tableKeyRef = useRef(crypto.randomUUID());
  const searchTimerRef = useRef(null);
  const debouncedSearchRef = useRef("");

  useEffect(() => {
    dispatch(
      fetchFilters({
        domain,
        loggedInUserEmail: actingUserEmail,
        status: statusFilter,
        superRegion: superRegionFilter,
        processingView: processingViewId,
      })  
    );
  }, [dispatch, domain, actingUserEmail, statusFilter, superRegionFilter, processingViewId]);

  const dispatchFetchQueue = useCallback(
    (overrides = {}) => {
      dispatch(
        fetchQueue({
          domain,
          pageNumber: 1,
          search: debouncedSearchRef.current || null,
          superRegion: superRegionFilter,
          countryRegion: countryRegionFilter,
          office: officeFilter,
          sector: sectorFilter,
          status: statusFilter ? [statusFilter] : [],
          clientName: clientNameFilter,
          date: dateFilter,
          loggedInUserEmail: actingUserEmail,
          processingView: processingViewId,
          ...overrides,
        }),
      );
    },
    [
      dispatch,
      domain,
      superRegionFilter,
      countryRegionFilter,
      officeFilter,
      sectorFilter,
      statusFilter,
      clientNameFilter,
      dateFilter,
      actingUserEmail,
      processingViewId,
    ],
  );

  useEffect(() => {
    dispatchFetchQueue();
  }, [dispatchFetchQueue]);

  useEffect(() => {
  dispatch(
    fetchCountriesThunk({
      superRegionIDs: [0],
      regionIDs: [0],
    }),
  );
}, [dispatch]);



    const handleSearchChange = (event) => {
    const value = event?.target ? event.target.value : event;
    setSearchTerm(value || "");

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      debouncedSearchRef.current = value || "";
      dispatchFetchQueue({ search: value || null });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    debouncedSearchRef.current = "";
    dispatchFetchQueue({ search: null });
  };

  const handleSelectionChanged = useCallback((event) => {
    const selected = event.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  const handleAssign = async ({ email, firstName, lastName }) => {
    const requestIds = selectedRows.map((row) => row.requestID).filter(Boolean);

    if (requestIds.length === 0) return;

    const result = await dispatch(
      assignEM({
        requestIds,
        engagementManagerEmail: email,
        firstName,
        lastName,
        actingUserEmail,
        domain,
      }),
    );

    if (!result.error) {
      setAssignError("");
      setShowAssignModal(false);
      setSelectedRows([]);
      dispatchFetchQueue();
      setToastMessage("Engagement Manager assigned successfully!");
      setToastVariant("success");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } else {
      const errMsg =
        typeof result.payload === "string"
          ? result.payload
          : result.payload?.message || "Failed to assign Engagement Manager";
      setAssignError(errMsg);
    }
  };

  const handleRowClick = useCallback(
    (event) => {
      if (!domain || !event?.data) return;

      const isSelectionColumn = Boolean(
        event?.colDef?.checkboxSelection ||
        event?.colDef?.headerCheckboxSelection,
      );
      if (isSelectionColumn) return;

      navigate(`/${domain}/${routes.viewDetails}`, {
        state: { engagementData: event.data, from: routes.resourceManager },
      });
    },
    [domain, navigate],
  );

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const dto = {
        search: debouncedSearchRef.current || null,
        superRegion: toArrayOrNull(superRegionFilter),
        countryRegion: toArrayOrNull(countryRegionFilter),
        office: toArrayOrNull(officeFilter),
        sector: toArrayOrNull(sectorFilter),
        status: statusFilter ? [statusFilter] : null,
        clientName: toArrayOrNull(clientNameFilter),
        startDate: toDateString(dateFilter?.[0]) || null,
        endDate: toDateString(dateFilter?.[1]) || null,
        loggedInUserEmail: actingUserEmail || null,
        processingView: processingViewId,
      };
      await exportResourceManagerQueue(dto, domain);
    } catch {
      setToastMessage("Failed to download Excel");
      setToastVariant("error");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setExportLoading(false);
    }
  };
  
  const processingViewOptions = useMemo(() => {
  const options = [];

  if (isGdsUser) {
    options.push("GDS");
  }

  if (isOnshoreUser) {
    assignedCountries.forEach((country) => {
      options.push(country.countryName);
    });
  }

  return options;
}, [
  isGdsUser,
  isOnshoreUser,
  assignedCountries,
]);

  const columnDefs = useMemo(
    () => getColumnDefs(t, { showCheckbox: !isAssigned }),
    [t, isAssigned],
  );

  const countLabel = String(totalRecords).padStart(2, "0");

  return (
    <div className="resource-manager">
      <div className="rmq-topbar">
        <div className="rmq-topbar-title">
          {t("rmq_pageTitlePMO")}
          <span className="rmq-topbar-count">({countLabel})</span>
        </div>
        <div className="rmq-topbar-actions">
          <button
            className={`rmq-outline-btn${isAssigned || selectedRows.length === 0 ? " rmq-outline-btn--disabled" : ""}`}
            type="button"
            disabled={isAssigned || selectedRows.length === 0}
            onClick={() => setShowAssignModal(true)}
          >
            <span className="rmq-outline-btn-icon">
              <UserCircleIcon size={16} color="currentColor" />
            </span>
            {t("rmq_assignEngagementManager")}
          </button>
          <button
            className={`rmq-outline-btn${exportLoading ? " rmq-outline-btn--disabled" : ""}`}
            type="button"
            disabled={exportLoading}
            onClick={handleExportExcel}
          >
            <span className="rmq-outline-btn-icon">
              <DownloadIcon />
            </span>
            {t("rmq_downloadExcel")}
          </button>
        </div>
      </div>

      <div className="rmq-filters-card">
        <div className="rmq-filters-grid">
        {showProcessingViewDropdown && (
            <div className="rmq-filter-field">
              <label className="rmq-filter-label">
                Processing Team
              </label>

              <Select
                key={`pv-${processingViewOptions.length}`}
                placeholder={t("rmq_all")}
                options={processingViewOptions.map((opt) => ({
                  id: opt,
                  name: opt,
                  selected: processingViewFilter.includes(opt),
                }))}
                value={processingViewFilter}
                onChange={handleFilterChange(setProcessingViewFilter)}
                multiple
              />
            </div>
          )}
          <div className="rmq-filter-field">
            <label className="rmq-filter-label">{t("rmq_superRegion")}</label>
            <Select
              key={`sr-${filterOptions.superRegions.length}`}
              placeholder={t("rmq_all")}
              options={filterOptions.superRegions}
              value={superRegionFilter}
              onChange={handleFilterChange(setSuperRegionFilter)}
              multiple
            />
          </div>
          <div className="rmq-filter-field">
            <label className="rmq-filter-label">{t("rmq_countryRegion")}</label>
            <Select
              key={`cr-${filterOptions.countryRegions.length}`}
              placeholder={t("rmq_all")}
              options={filterOptions.countryRegions}
              value={countryRegionFilter}
              onChange={handleFilterChange(setCountryRegionFilter)}
              multiple
            />
          </div>
          <div className="rmq-filter-field">
            <label className="rmq-filter-label">{t("rmq_office")}</label>
            <Select
              key={`of-${filterOptions.offices.length}`}
              placeholder={t("rmq_all")}
              options={filterOptions.offices}
              value={officeFilter}
              onChange={handleFilterChange(setOfficeFilter)}
              multiple
            />
          </div>
          <div className="rmq-filter-field">
            <label className="rmq-filter-label">{t("rmq_sector")}</label>
            <Select
              key={`sc-${filterOptions.sectors.length}`}
              placeholder={t("rmq_all")}
              options={filterOptions.sectors}
              value={sectorFilter}
              onChange={handleFilterChange(setSectorFilter)}
              multiple
            />
          </div>
          <div className="rmq-filter-field">
            <label className="rmq-filter-label">{t("rmq_status")}</label>
            <Select
              key={`st-${filterOptions.statuses.length}`}
              placeholder={t("rmq_all")}
              options={filterOptions.statuses}
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
            />
          </div>
          <div className="rmq-filter-field">
            <label className="rmq-filter-label">{t("rmq_clientName")}</label>
            <Select
              key={`cn-${filterOptions.clientNames.length}`}
              placeholder={t("rmq_all")}
              options={filterOptions.clientNames}
              value={clientNameFilter}
              onChange={handleFilterChange(setClientNameFilter)}
              multiple
            />
          </div>
          <div className="rmq-filter-field">
            <div className="rmq-filter-label-row">
              <label className="rmq-filter-label">{t("rmq_dateRange")}</label>
              <MotifTooltip
                trigger={
                  <span
                    className="rmq-filter-label-info"
                    aria-label={t("rmq_dateRange_tooltip", {
                      defaultValue:
                        "Data is being filtered based on Expected Completion Date",
                    })}
                    tabIndex={0}
                  >
                    <HelpIcon />
                  </span>
                }
                allowClick={false}
                allowHover={true}
                flip={true}
                hideCloseButton={true}
              >
                {t("rmq_dateRange_tooltip", {
                  defaultValue:
                    "Data is being filtered based on Expected Completion Date",
                })}
              </MotifTooltip>
            </div>
            <MotifFormField className="rmq-datepicker-form-field">
              <MotifDatePicker
                value={dateFilter}
                onChange={(val) => setDateFilter(val || [null, null])}
                format="dd/MM/yyyy"
                range
                clearIcon="✕"
              />
            </MotifFormField>
          </div>
          <div className="rmq-filter-field">
            <SearchInput
              placeholder={t("search")}
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
          </div>
        </div>
      </div>

      <div className="rmq-table-wrap">
        <Table
          rowData={rows}
          colDefs={columnDefs}
          pagesize={10}
          rowSelect={isAssigned ? undefined : "multiple"}
          rowHeight={44}
          headerHeight={44}
          selectionChanged={handleSelectionChanged}
          forceUpdate={tableKeyRef.current}
          loading={loading}
          rowClicked={handleRowClick}
          noRowsMessage="No record found"
        />
      </div>

      <AssignEngagementManagerModal
        show={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignError("");
        }}
        onAssign={handleAssign}
        loading={assignLoading}
        error={assignError}
        onClearError={() => setAssignError("")}
        note={t("modal_assignEM_note", {
          defaultValue:
            "Ensure that you assign requests to employees who are either Seniors or higher ups.",
        })}
      />

      {showToast && (
        <ToastMessage
          variant={toastVariant}
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}

export default ResourceManager;
